import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Md5 } from 'ts-md5/dist/md5';
import { environment } from '../../../environments/environment';
import { LoginFlowResponse, MfaChallenge, MfaVerifyResult, PasswordStepResult, SessionTokens, McpHandoffResponse } from './auth-flow.model';
import { toMfaChallenge, toMfaVerifyResult, toPasswordStepResult, toSessionTokens } from './auth-flow.mapper';
import { TrustedDeviceService } from './trusted-device.service';

const SKIP_INTERCEPTOR = { skip: 'true' };

const LOGIN_SOURCE = 'web';

// los 4 hosts de dev comparten el build de environment.dev.ts, así que el cliente se resuelve del host
const CLIENT_BY_HOSTNAME: Record<string, string> = {
  localhost: 'local',
  '127.0.0.1': 'local',
  'fidelity-dev.myhotel.cl': 'default',
  'fidelity-dev-estrategica.myhotel.cl': 'estrategica',
  'fidelity-dev-continuidad.myhotel.cl': 'continuidad',
  'fidelity-dev-shapeup.myhotel.cl': 'shapeup',
};

@Injectable({ providedIn: 'root' })
export class AuthFlowService {
  private readonly http = inject(HttpClient);
  private readonly trustedDevice = inject(TrustedDeviceService);

  private readonly base = environment.securityPath;

  resolveFlow(username: string): Observable<LoginFlowResponse> {
    let params = new HttpParams().set('source', LOGIN_SOURCE);

    const client = CLIENT_BY_HOSTNAME[window.location.hostname.toLowerCase()];
    if (client) params = params.set('client', client);

    return this.http.post<LoginFlowResponse>(
      `${this.base}login/flow`,
      { username },
      { headers: SKIP_INTERCEPTOR, params },
    );
  }

  loginWithPassword(username: string, password: string): Observable<PasswordStepResult> {
    const headers: Record<string, string> = { ...SKIP_INTERCEPTOR };
    const trustedDeviceToken = this.trustedDevice.tokenFor(username);
    if (trustedDeviceToken) headers['X-Trusted-Device-Token'] = trustedDeviceToken;

    return this.http
      .post(`${this.base}login/flow/password`, { username, password: Md5.hashStr(password) }, { headers })
      .pipe(map(toPasswordStepResult));
  }

  verifyMfa(challengeId: string, code: string, rememberDevice: boolean): Observable<MfaVerifyResult> {
    return this.http
      .post(
        `${this.base}login/mfa/verify`,
        { challenge_id: challengeId, code, remember_device: rememberDevice },
        { headers: SKIP_INTERCEPTOR },
      )
      .pipe(map(toMfaVerifyResult));
  }

  resendMfa(challengeId: string): Observable<MfaChallenge | null> {
    return this.http
      .post(`${this.base}login/mfa/resend`, { challenge_id: challengeId }, { headers: SKIP_INTERCEPTOR })
      .pipe(map(toMfaChallenge));
  }

  exchangeSso(code: string, state: string): Observable<SessionTokens | null> {
    return this.http
      .post(`${this.base}sso/exchange`, { code, state }, { headers: SKIP_INTERCEPTOR })
      .pipe(map(toSessionTokens));
  }

  createMcpHandoff(mcpTransaction: string, accessToken: string): Observable<McpHandoffResponse> {
    return this.http.post<McpHandoffResponse>(
      `${environment.mcpPath}handoff`,
      { mcp_transaction: mcpTransaction },
      { headers: { ...SKIP_INTERCEPTOR, Authorization: `Bearer ${accessToken}` } },
    );
  }
}
